import { create } from 'zustand';
import apiClient from '@/services/apiClient';

// Type definitions matching the backend data
export interface Tenancy {
    tenancyId: string;
    propertyId: string;
    unitName?: string;
    propertyName?: string;
    status: string;
}

export interface Invitation {
    id: string;
    property_name?: string;
    unit_number?: string;
    rent_amount: number;
    lease_start_date: string;
}

export interface DashboardData {
    rent: {
        amountDue: number;
        dueDate: string;
        status: "Paid" | "Due" | "Overdue";
    };
    notices: any[];
    recentTransactions: any[];
}

interface TenantState {
    dashboardData: DashboardData | null;
    tenancies: Tenancy[];
    invitations: Invitation[];
    loading: boolean;
    error: string | null;
    hasMultipleTenancies: boolean;
    agreementAccepted: boolean;
    selectedTenancyId: string | null;

    fetchTenancies: () => Promise<void>;
    fetchInvitations: () => Promise<void>;
    fetchDashboardData: () => Promise<void>;
    selectTenancy: (tenancyId: string, propertyId: string) => void;
    checkAndInitializeState: () => Promise<void>;
}

export const useTenantStore = create<TenantState>((set, get) => ({
    dashboardData: null,
    tenancies: [],
    invitations: [],
    loading: false,
    error: null,
    hasMultipleTenancies: false,
    agreementAccepted: false,
    selectedTenancyId: localStorage.getItem("selectedTenancyId"),

    fetchTenancies: async () => {
        try {
            const response = await apiClient.get('/tenants/my-tenancies');
            set({ 
                tenancies: response.data || [], 
                hasMultipleTenancies: (response.data?.length || 0) > 1 
            });
        } catch (error) {
            console.error("Error fetching tenancies", error);
        }
    },

    fetchInvitations: async () => {
        try {
            const response = await apiClient.get('/tenants/my-invitations');
            set({ invitations: response.data || [] });
        } catch (error) {
            console.error("Error fetching invitations", error);
        }
    },

    fetchDashboardData: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get('/tenants/dashboard');
            set({ dashboardData: response.data, agreementAccepted: true, loading: false });
        } catch (error: any) {
            if (error.response?.data?.code === "AGREEMENT_NOT_ACCEPTED") {
                set({ agreementAccepted: false, loading: false });
            } else {
                set({ error: "Could not load dashboard data.", loading: false });
            }
        }
    },

    selectTenancy: (tenancyId: string, propertyId: string) => {
        localStorage.setItem("selectedTenancyId", tenancyId);
        localStorage.setItem("selectedPropertyId", propertyId);
        set({ selectedTenancyId: tenancyId });
    },

    checkAndInitializeState: async () => {
        set({ loading: true });
        try {
            await get().fetchTenancies();
            await get().fetchInvitations();

            const state = get();
            if (state.tenancies.length > 0) {
                const currentSelection = localStorage.getItem("selectedTenancyId");
                const validSelection = currentSelection && state.tenancies.some(t => t.tenancyId === currentSelection);

                if (!validSelection && state.tenancies.length === 1) {
                    const single = state.tenancies[0];
                    get().selectTenancy(single.tenancyId, single.propertyId);
                }
            }

            if (state.invitations.length === 0) {
                await get().fetchDashboardData();
            } else {
                 set({ loading: false });
            }
        } catch (error) {
            set({ loading: false, error: "Initialization failed" });
        }
    }
}));
